import { useEffect, useCallback, useMemo } from 'react';
import { useAtom, useSetAtom } from 'jotai';
import {
  EmptySessionInfo,
  SessionTimelineTableItem,
  SessionTimelineTableHeader,
} from '@conviva/organism-components';
import { highlightedSessionIdsAtom, selectedFlowAtom, selectedSessionAtom } from '../../atom';
import { useGetTimelineSummary } from '../../utils/queryHooks';
import { DEVICE_NAME_FIELD, USER_ID_FIELD } from '../../constants';

export const useSelectedSession = ({
  impacted,
  sessionTableData,
  viewerId,
  interval,
  clientId,
  idGroupData,
}: {
  impacted: boolean;
  sessionTableData: any[];
  viewerId?: string[];
  interval?: string;
  clientId?: string | null;
  idGroupData?: any[];
}) => {
  const [selectedSession, setSelectedSession] = useAtom(selectedSessionAtom);
  const setHighlightedSessionIds = useSetAtom(highlightedSessionIdsAtom);
  const setSelectedFlow = useSetAtom(selectedFlowAtom);

  // Get raw summary data for enrichment (before processing)
  const { data: summaryData } = useGetTimelineSummary(
    viewerId || [],
    interval || '',
    clientId || null,
  );

  const handleSelectedSessionChange = useCallback(
    (v: SessionTimelineTableItem) => {
      setSelectedSession(v);
      setHighlightedSessionIds([v.sessionId]);
      setSelectedFlow(null);
    },
    [setHighlightedSessionIds, setSelectedFlow, setSelectedSession],
  );

  useEffect(() => {
    let item: SessionTimelineTableItem = EmptySessionInfo;
    let first: SessionTimelineTableItem = EmptySessionInfo;
    for (const i of sessionTableData) {
      if ((i as SessionTimelineTableHeader).isHeader) {
        continue;
      }
      if (first === EmptySessionInfo) {
        first = i as SessionTimelineTableItem;
      }
      // if impacted, set the first impacted session as selected
      if (impacted && (i as SessionTimelineTableItem).impacted !== 'Impacted') {
        continue;
      }
      item = i as SessionTimelineTableItem;
      break;
    }
    if (item === EmptySessionInfo) {
      item = first;
    }
    handleSelectedSessionChange(item);
  }, [sessionTableData, impacted, handleSelectedSessionChange]);

  // Extract Device Name and User ID from idGroupData
  const deviceNameAndUserId = useMemo(() => {
    const result: { deviceName?: string; userId?: string } = {};

    if (idGroupData && Array.isArray(idGroupData)) {
      // Find Device Name field
      const deviceNameField = idGroupData.find((item) => item.titleValue === 'Device Name');
      if (deviceNameField?.value && deviceNameField.value !== 'N/A') {
        result.deviceName = deviceNameField.value;
      }

      // Find User ID field
      const userIdField = idGroupData.find((item) => item.titleValue === 'User ID');
      if (userIdField?.value && userIdField.value !== 'N/A') {
        result.userId = userIdField.value;
      }
    }

    // Also check if device_name or app_user_id are already in selectedSession
    if (selectedSession) {
      if (!result.deviceName && selectedSession[DEVICE_NAME_FIELD]) {
        result.deviceName = selectedSession[DEVICE_NAME_FIELD];
      }
      if (!result.userId && selectedSession[USER_ID_FIELD]) {
        result.userId = selectedSession[USER_ID_FIELD];
      }
    }

    return result;
  }, [idGroupData, selectedSession]);

  // Enrich selectedSession with summary data, Device Name, and User ID
  const enrichedSelectedSession = useMemo(() => {
    if (!selectedSession || !selectedSession.sessionId) {
      return selectedSession;
    }

    const enrichment: Record<string, any> = {};

    // Add Device Name and User ID
    if (deviceNameAndUserId.deviceName) {
      enrichment.deviceName = deviceNameAndUserId.deviceName;
      enrichment['Device Name'] = deviceNameAndUserId.deviceName;
    }
    if (deviceNameAndUserId.userId) {
      enrichment.userId = deviceNameAndUserId.userId;
      enrichment['User ID'] = deviceNameAndUserId.userId;
      enrichment[USER_ID_FIELD] = deviceNameAndUserId.userId;
    }

    // Transform userInfoData array into flat object properties
    // e.g., [{ name: "City", value: "Bengaluru" }] -> { City: "Bengaluru" }
    const userInfoMap: Record<string, string | number> = {};
    if (summaryData?.userInfoData && Array.isArray(summaryData.userInfoData)) {
      summaryData.userInfoData.forEach((item) => {
        if (item.name && item.value !== undefined) {
          userInfoMap[item.name] = item.value;
        }
      });
    }

    // Transform eventSummaryData array into flat object properties
    // e.g., [{ name: "conviva_button_click", value: 331 }] -> { "conviva_button_click": 331 }
    const eventSummaryMap: Record<string, string | number> = {};
    if (summaryData?.eventSummaryData && Array.isArray(summaryData.eventSummaryData)) {
      summaryData.eventSummaryData.forEach((item) => {
        if (item.name && item.value !== undefined) {
          eventSummaryMap[item.name] = item.value;
        }
      });
    }

    // Merge summary data into selectedSession
    return {
      ...selectedSession,
      // Device Name and User ID
      ...enrichment,
      // User Info properties (flattened)
      ...userInfoMap,
      // Event Summary properties (flattened)
      ...eventSummaryMap,
      // Keep original arrays for backward compatibility if needed
      userInfoData: summaryData?.userInfoData,
      eventSummaryData: summaryData?.eventSummaryData,
    } as SessionTimelineTableItem;
  }, [selectedSession, summaryData, deviceNameAndUserId]);

  return {
    selectedSession: enrichedSelectedSession,
    handleSelectedSessionChange,
  };
};
